// src/components/Sidebar/SidebarRight.jsx

import React, { useEffect } from 'react'
import {
  ArrowsUpDownIcon,
  CalculatorIcon,
  HeartIcon,
  TableCellsIcon,
  TagIcon,
} from '@heroicons/react/24/outline'
import { useSidebar } from '../../contexts/SidebarContext'
import { useMatch } from 'react-router-dom'

const TABS = [
  {
    id: 'ingredients',
    label: 'Ingredients',
    icon: TableCellsIcon,
    component: React.lazy(() => import('./IngredientsTab')),
  },
  {
    id: 'costing',
    label: 'Costing',
    icon: CalculatorIcon,
    component: React.lazy(() => import('./CostingTab')),
  },
  {
    id: 'nutrition',
    label: 'Nutrition',
    icon: HeartIcon,
    component: React.lazy(() => import('./NutritionTab')),
  },
  {
    id: 'scaling',
    label: 'Scaling',
    icon: ArrowsUpDownIcon,
    component: React.lazy(() => import('./ScalingTab')),
  },
  {
    id: 'dietary',
    label: 'Dietary Tags',
    icon: TagIcon,
    component: React.lazy(() => import('./DietaryTags')),
  },
]

export default function SidebarRight() {
  const {
    activeUtilitiesTab,
    setActiveUtilitiesTab,
    sidebarTabCounts,
    } = useSidebar()

  // Only render Ingredients utilities when we are inside a recipe route
  // IMPORTANT: call hooks unconditionally (no short-circuiting)
  const matchEdit = useMatch('/recipes/:id/edit')
  const matchNew = useMatch('/recipes/new/edit')
  const matchPrint = useMatch('/recipes/:id/print')

  const inRecipeContext = !!(matchEdit || matchNew || matchPrint)

  // 🔥 ANDROID BACK BUTTON INTEGRATION
  // Expose a global function so native back button can close this sidebar
  useEffect(() => {
    window.closeRightSidebar = () => {
      setActiveUtilitiesTab(null) // null = closed
    }

    return () => {
      // tidy up (optional)
      if (window.closeRightSidebar) delete window.closeRightSidebar
    }
  }, [setActiveUtilitiesTab])

    const currentTab = TABS.find(t => t.id === activeUtilitiesTab) || TABS[0]

  const ActiveComp = currentTab.component

  return (
    <aside
      className="
        flex flex-col h-full
        w-full flex-none
        bg-very-light-gray text-charcoal
        border-l border-light-gray
      "
      aria-label="Right sidebar navigation"
    >
      {/* ─── Icon Tabs ─────────────────────────── */}
      <nav
        role="tablist"
        className="flex justify-between md:justify-around items-center border-b border-light-gray py-2 px-2"
      >
        {TABS.map(tab => {
          const isActive = tab.id === activeUtilitiesTab
          const Icon = tab.icon
          const isIngredients = tab.id === 'ingredients'
          const isDisabled = isIngredients && !inRecipeContext

          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-label={tab.label}
              title={isDisabled ? 'Open a recipe to use Ingredients Tagger' : tab.label}
              disabled={isDisabled}
              onClick={() => {
                if (isDisabled) return
                setActiveUtilitiesTab(tab.id)
              }}
              className={`
                relative flex items-center
                ${isActive ? 'px-4' : 'px-2'} py-3 md:py-3 rounded transition
                focus:outline-none focus-visible:ring-2 focus-visible:ring-rich-gold
                ${isActive
                  ? 'bg-white text-deep-blue shadow-inner ring-2 ring-ring-rich-gold'
                  : 'text-charcoal hover:bg-soft-yellow/40 hover:text-rich-gold'}
                ${isDisabled ? 'opacity-40 cursor-not-allowed hover:bg-transparent hover:text-charcoal' : ''}
              `}
            >
              <Icon className="w-5 h-5" />

              {isActive && (
                <span className="ml-2 max-w-[80px] truncate whitespace-nowrap text-sm font-medium">
                  {tab.label}
                </span>
              )}

              {sidebarTabCounts?.[tab.id] > 0 && (
                <span className="absolute -top-1 -right-1 text-xs bg-rich-gold text-white rounded-full px-1">
                  {sidebarTabCounts[tab.id]}
                </span>
              )}
            </button>
          )
        })}
      </nav>

      {/* ─── Content Panel ────────────────────────── */}
      <div
        role="tabpanel"
        aria-labelledby={`tab-${currentTab.id}`}
        className="flex-1 overflow-y-auto"
      >
        <React.Suspense
          fallback={
            <div className="p-unit text-sm text-slate-gray text-center">
              Loading…
            </div>
          }
        >
          {currentTab.id === 'ingredients' && !inRecipeContext ? (
            <div className="p-unit text-sm text-slate-gray text-center">
              Open a recipe to use Ingredients Tagger.
            </div>
          ) : (
            <ActiveComp />
          )}
        </React.Suspense>
      </div>
    </aside>
  )
}
